<template>
  <AcademyLayout @enroll="enrollOpen = true">
    <div class="ac-wrap">
      <section class="ac-mine-head">
        <h1>我的学习</h1>
        <p>继续学习已报名课程，查看学习进度与最近学习记录</p>
      </section>

      <div class="ac-mine-stats">
        <div class="ac-stat">
          <strong>{{ items.length }}</strong>
          <span>全部课程</span>
        </div>
        <div class="ac-stat">
          <strong>{{ learningCount }}</strong>
          <span>学习中</span>
        </div>
        <div class="ac-stat">
          <strong>{{ doneCount }}</strong>
          <span>已完成</span>
        </div>
      </div>

      <div class="ac-filter">
        <button
          v-for="f in filters"
          :key="f.value"
          type="button"
          class="ac-filter__btn"
          :class="{ 'ac-filter__btn--active': filter === f.value }"
          @click="filter = f.value"
        >
          {{ f.label }}
        </button>
      </div>

      <div v-if="filtered.length" class="ac-mine-list">
        <article v-for="item in filtered" :key="item.courseId" class="ac-mine-card">
          <div class="ac-mine-card__cover" :class="`ac-mine-card__cover--${item.course.coverTone}`" />
          <div class="ac-mine-card__body">
            <div class="ac-mine-card__top">
              <div class="ac-mine-card__titles">
                <p v-if="item.course.shortTitle" class="ac-mine-card__short">{{ item.course.shortTitle }}</p>
                <h3>{{ item.course.title }}</h3>
              </div>
              <span class="ac-mine-card__status" :class="`ac-mine-card__status--${item.status}`">
                {{ item.status === 'done' ? '已完成' : '学习中' }}
              </span>
            </div>
            <p class="ac-mine-card__last">最近学习：{{ item.lastStudy }}</p>
            <div class="ac-progress">
              <div class="ac-progress__bar">
                <i :style="{ width: `${item.progress}%` }" />
              </div>
              <span>{{ item.progress }}%</span>
            </div>
            <div class="ac-mine-card__actions">
              <button type="button" class="ac-btn ac-btn--sm" @click="goCourse(item.courseId)">
                {{ item.status === 'done' ? '再看一遍' : '继续学习' }}
              </button>
              <button
                v-if="item.course.scheduleRoute"
                type="button"
                class="ac-btn ac-btn--sm ac-btn--ghost"
                @click="goSchedule(item.course.scheduleId)"
              >
                培训安排
              </button>
            </div>
          </div>
        </article>
      </div>

      <div v-else class="ac-mine-empty">
        <p>暂无符合条件的课程</p>
        <router-link :to="{ name: 'training' }" class="ac-btn">去选课</router-link>
      </div>

      <section class="ac-mine-tasks">
        <h2>学习任务</h2>
        <ul>
          <li>
            <span>完成高仁综合实训基础测评</span>
            <router-link :to="{ name: 'training-exam' }" class="ac-btn ac-btn--sm ac-btn--ghost">去考试</router-link>
          </li>
          <li>
            <span>下载培训配套知识资料</span>
            <router-link :to="{ name: 'training-knowledge' }" class="ac-btn ac-btn--sm ac-btn--ghost">去资料库</router-link>
          </li>
          <li>
            <span>查看10天培训安排</span>
            <router-link :to="{ name: 'training-schedule' }" class="ac-btn ac-btn--sm ac-btn--ghost">看安排</router-link>
          </li>
        </ul>
      </section>
    </div>

    <AcademyEnrollModal v-model="enrollOpen" :course="null" />
  </AcademyLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import AcademyLayout from '../components/AcademyLayout.vue'
import AcademyEnrollModal from '../components/AcademyEnrollModal.vue'
import { getAcademyCourse, myLearningDemo } from '../data/trainingCatalog'

const router = useRouter()
const filter = ref('all')
const enrollOpen = ref(false)

const filters = [
  { label: '全部', value: 'all' },
  { label: '学习中', value: 'learning' },
  { label: '已完成', value: 'done' },
]

const items = computed(() =>
  myLearningDemo
    .map((row) => ({
      ...row,
      course: getAcademyCourse(row.courseId),
    }))
    .filter((row) => row.course)
)

const filtered = computed(() => {
  if (filter.value === 'all') return items.value
  return items.value.filter((i) => i.status === filter.value)
})

const learningCount = computed(() => items.value.filter((i) => i.status === 'learning').length)
const doneCount = computed(() => items.value.filter((i) => i.status === 'done').length)

function goCourse(id) {
  router.push({ name: 'training-course', params: { courseId: id } })
}

function goSchedule(scheduleId) {
  router.push({
    name: 'training-schedule',
    query: scheduleId ? { schedule: scheduleId } : undefined,
  })
}
</script>

<style scoped>
.ac-wrap {
  width: min(1160px, calc(100% - 40px));
  margin: 0 auto;
}

.ac-mine-head {
  padding: 28px 0 8px;
}

.ac-mine-head h1 {
  margin: 0 0 8px;
  font-size: 24px;
  color: #222;
}

.ac-mine-head p {
  margin: 0;
  font-size: 14px;
  color: #999;
}

.ac-mine-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 20px 0;
}

.ac-stat {
  background: #fff;
  border-radius: 6px;
  padding: 18px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.ac-stat strong {
  display: block;
  font-size: 28px;
  color: #2d8cf0;
  margin-bottom: 4px;
}

.ac-stat span {
  font-size: 13px;
  color: #999;
}

.ac-filter {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.ac-filter__btn {
  padding: 7px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
}

.ac-filter__btn--active {
  border-color: #2d8cf0;
  color: #2d8cf0;
  background: #f3f8ff;
}

.ac-mine-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ac-mine-card {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 18px;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 14px;
}

.ac-mine-card__cover {
  border-radius: 4px;
  overflow: hidden;
  aspect-ratio: 16 / 10;
  background: linear-gradient(145deg, #2d8cf0, #5babff);
}

.ac-mine-card__cover--2 { background: linear-gradient(145deg, #19be6b, #47d08a); }
.ac-mine-card__cover--3 { background: linear-gradient(145deg, #ff6a00, #ff9345); }
.ac-mine-card__cover--4 { background: linear-gradient(145deg, #2b85e4, #17233d); }

.ac-mine-card__cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.88;
}

.ac-mine-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.ac-mine-card__titles {
  min-width: 0;
}

.ac-mine-card__short {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  color: #2d8cf0;
}

.ac-mine-card__top h3 {
  margin: 0;
  font-size: 16px;
  color: #222;
  line-height: 1.4;
}

.ac-mine-card__status {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 2px;
  font-size: 12px;
  background: #eaf4ff;
  color: #2d8cf0;
}

.ac-mine-card__status--done {
  background: #e8f8ef;
  color: #19be6b;
}

.ac-mine-card__last {
  margin: 10px 0 12px;
  font-size: 13px;
  color: #999;
}

.ac-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.ac-progress__bar {
  flex: 1;
  height: 6px;
  background: #f0f0f0;
  border-radius: 999px;
  overflow: hidden;
}

.ac-progress__bar i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #2d8cf0, #5babff);
  border-radius: 999px;
}

.ac-progress span {
  font-size: 12px;
  color: #666;
  min-width: 36px;
}

.ac-mine-card__actions {
  display: flex;
  gap: 10px;
}

.ac-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 22px;
  border: none;
  border-radius: 4px;
  background: #ff6a00;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}

.ac-btn--sm {
  padding: 7px 16px;
  font-size: 13px;
}

.ac-btn--ghost {
  background: #fff;
  color: #ff6a00;
  border: 1px solid #ff6a00;
}

.ac-mine-empty {
  padding: 60px 20px;
  text-align: center;
  background: #fff;
  border-radius: 6px;
  color: #999;
}

.ac-mine-empty .ac-btn {
  margin-top: 16px;
}

.ac-mine-tasks {
  margin-top: 20px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 18px 20px;
}

.ac-mine-tasks h2 {
  margin: 0 0 12px;
  font-size: 16px;
  color: #222;
}

.ac-mine-tasks ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ac-mine-tasks li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #333;
}

.ac-mine-tasks li:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

@media (max-width: 760px) {
  .ac-wrap {
    width: calc(100% - 24px);
  }

  .ac-mine-card {
    grid-template-columns: 1fr;
  }

  .ac-mine-stats {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
